import React, { useState, useRef } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader2,
  Building2,
  Users,
  AlertCircle,
  Info,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status options
const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' }
];

// Lead Type options
const LEAD_TYPE_OPTIONS = [
  { value: 'staffing_company', label: 'Staffing Company' },
  { value: 'client', label: 'Client' },
  { value: 'investor', label: 'Investor' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'misc', label: 'Misc' }
];

// Company fields
const COMPANY_FIELDS = [
  { key: 'name', label: 'Company Name', required: true },
  { key: 'address1', label: 'Address Line 1' },
  { key: 'address2', label: 'Address Line 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP Code' },
  { key: 'url', label: 'Website URL' },
  { key: 'revenues', label: 'Revenues', hint: 'Annual revenue in dollars' },
  { key: 'industry', label: 'Industry' },
  { key: 'ats_used', label: 'ATS Used' },
  { key: 'company_notes', label: 'Company Notes' },
];

// Lead fields
const LEAD_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name' },
  { key: 'work_email', label: 'Work Email' },
  { key: 'personal_email', label: 'Personal Email' },
  { key: 'work_direct_phone', label: 'Work Phone' },
  { key: 'mobile_phone', label: 'Mobile Phone' },
  { key: 'company_name', label: 'Company Name', required: true },
  { key: 'title', label: 'Title' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'x_twitter_url', label: 'X/Twitter URL' },
  { key: 'linkedin_url_x', label: 'LinkedIn URL-X' },
  { key: 'location', label: 'Location' },
  { key: 'source', label: 'Source' },
  { key: 'description', label: 'Notes' },
  { key: 'status', label: 'Status', hint: 'new, contacted, qualified, closed_won, closed_lost' },
  { key: 'lead_types', label: 'Lead Types', hint: 'Semicolon-separated' },
];

export default function CSVImportWizard({ open, onOpenChange, onImportComplete }) {
  const [step, setStep] = useState(1); // 1 = Companies, 2 = Leads
  const [skipCompanies, setSkipCompanies] = useState(false);
  
  // Companies step state
  const [companyFile, setCompanyFile] = useState(null);
  const [companyCsvData, setCompanyCsvData] = useState(null);
  const [companyMappings, setCompanyMappings] = useState({});
  const [companyStaticValues, setCompanyStaticValues] = useState({});
  const [importingCompanies, setImportingCompanies] = useState(false);
  const [companyResults, setCompanyResults] = useState(null);
  
  // Leads step state
  const [leadFile, setLeadFile] = useState(null);
  const [leadCsvData, setLeadCsvData] = useState(null);
  const [leadMappings, setLeadMappings] = useState({});
  const [leadStaticValues, setLeadStaticValues] = useState({});
  const [importingLeads, setImportingLeads] = useState(false);
  const [leadResults, setLeadResults] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [duplicateCheckField, setDuplicateCheckField] = useState('work_email');
  
  const companyFileInputRef = useRef(null);
  const leadFileInputRef = useRef(null);

  // Reset state when modal closes
  const handleClose = () => {
    setStep(1);
    setSkipCompanies(false);
    setCompanyFile(null);
    setCompanyCsvData(null);
    setCompanyMappings({});
    setCompanyStaticValues({});
    setCompanyResults(null);
    setLeadFile(null);
    setLeadCsvData(null);
    setLeadMappings({});
    setLeadStaticValues({});
    setLeadResults(null);
    onOpenChange(false);
  };

  // Company file upload
  const handleCompanyFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      fastMode: false,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          toast.error('CSV file appears to be empty');
          return;
        }
        
        const headers = results.meta.fields || [];
        const rows = results.data;
        
        setCompanyFile(file);
        setCompanyCsvData({ headers, rows });
        
        // Auto-map: Find CSV columns that match system fields
        const autoMappings = {};
        COMPANY_FIELDS.forEach(field => {
          const matchingHeader = headers.find(header => {
            const headerLower = header.toLowerCase().trim();
            const fieldKeyLower = field.key.toLowerCase();
            const fieldLabelLower = field.label.toLowerCase();
            
            // Exact matches
            if (headerLower === fieldLabelLower || headerLower === fieldKeyLower) {
              return true;
            }
            
            // Special case: "Company" or "Company Name" should map to "name"
            if (fieldKeyLower === 'name' && (headerLower === 'company' || headerLower === 'company name' || headerLower === 'companyname')) {
              return true;
            }
            
            // Contains matches
            if (headerLower.includes(fieldKeyLower.replace('_', ' ')) || 
                fieldKeyLower.includes(headerLower.replace(' ', '_'))) {
              return true;
            }
            
            return false;
          });
          if (matchingHeader) {
            autoMappings[field.key] = matchingHeader;
          }
        });
        
        setCompanyMappings(autoMappings);
        setCompanyStaticValues({});
        toast.success(`Loaded ${rows.length} companies from CSV`);
      },
      error: (error) => {
        toast.error(`Failed to parse CSV: ${error.message}`);
      }
    });
  };

  // Lead file upload
  const handleLeadFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    // Read file as text first, then parse with PapaParse
    const reader = new FileReader();
    reader.onload = (event) => {
      const csvText = event.target.result;
      
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: 'greedy',
        delimiter: ',',
        newline: '\n',
        quoteChar: '"',
        escapeChar: '"',
        fastMode: false,
        complete: (results) => {
          if (!results.data || results.data.length === 0) {
            toast.error('CSV file appears to be empty');
            return;
          }
          
          const headers = results.meta.fields || [];
          const rows = results.data;
          
          console.log(`PapaParse results: ${rows.length} rows, ${headers.length} columns`);
          
          setLeadFile(file);
          setLeadCsvData({ headers, rows });
          
          // Auto-map: Find CSV columns that match system fields
          const autoMappings = {};
          LEAD_FIELDS.forEach(field => {
            const matchingHeader = headers.find(header => {
              const headerLower = header.toLowerCase().trim();
              const fieldKeyLower = field.key.toLowerCase();
              const fieldLabelLower = field.label.toLowerCase();
              
              // Exact matches
              if (headerLower === fieldLabelLower || headerLower === fieldKeyLower) {
                return true;
              }
              
              // Special case mappings for common variations
              if (fieldKeyLower === 'company_name' && (headerLower === 'company' || headerLower === 'companyname')) {
                return true;
              }
              if (fieldKeyLower === 'first_name' && (headerLower === 'firstname' || headerLower === 'first' || headerLower === 'title' || headerLower === 'name')) {
                // Map "Title" to first_name for CSVs where Title contains person's name
                return true;
              }
              if (fieldKeyLower === 'last_name' && (headerLower === 'lastname' || headerLower === 'last')) {
                return true;
              }
              if (fieldKeyLower === 'title' && (headerLower === 'job title' || headerLower === 'jobtitle' || headerLower === 'job_title' || headerLower === 'position')) {
                // Map "Job Title" to title field
                return true;
              }
              if (fieldKeyLower === 'work_email' && (headerLower === 'email' || headerLower === 'workemail' || headerLower === 'emailw')) {
                return true;
              }
              if (fieldKeyLower === 'linkedin_url' && (headerLower === 'linkedin' || headerLower === 'leadlinkedinurl' || headerLower === 'linkedin_url' || headerLower === 'normalizedurl')) {
                return true;
              }
              if (fieldKeyLower === 'x_twitter_url' && (headerLower === 'twitter' || headerLower === 'x' || headerLower === 'x_url' || headerLower === 'twitter_url')) {
                return true;
              }
              if (fieldKeyLower === 'work_direct_phone' && (headerLower === 'phone' || headerLower === 'phonew' || headerLower === 'work_phone')) {
                return true;
              }
              if (fieldKeyLower === 'mobile_phone' && (headerLower === 'mobile' || headerLower === 'phonem' || headerLower === 'cell')) {
                return true;
              }
              
              return false;
            });
            if (matchingHeader) {
              autoMappings[field.key] = matchingHeader;
            }
          });
          
          setLeadMappings(autoMappings);
          toast.success(`Loaded ${rows.length} leads from CSV`);
        },
        error: (error) => {
          toast.error(`Failed to parse CSV: ${error.message}`);
        }
      });
    };
    
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    
    reader.readAsText(file);
  };

  // Import companies
  const handleImportCompanies = async () => {
    if (!companyCsvData) {
      toast.error('Please upload a companies CSV file');
      return;
    }

    // Validate required fields are mapped
    const hasName = companyMappings['name'] || companyStaticValues.name;
    if (!hasName) {
      toast.error('Company Name is required. Please map it or provide a static value.');
      return;
    }

    setImportingCompanies(true);
    
    try {
      // Build import data
      const importData = companyCsvData.rows.map(row => {
        const mapped = {};
        
        // Apply field mappings (fieldKey -> csvColumn)
        Object.entries(companyMappings).forEach(([fieldKey, csvCol]) => {
          if (csvCol && csvCol !== 'skip') {
            mapped[fieldKey] = row[csvCol] || '';
          }
        });
        
        // Apply static values
        Object.entries(companyStaticValues).forEach(([fieldKey, value]) => {
          if (value) {
            mapped[fieldKey] = value;
          }
        });
        
        return mapped;
      });

      const response = await axios.post(`${API}/admin/import-companies`, {
        companies: importData,
        skip_duplicates: true
      });

      setCompanyResults(response.data);
      toast.success(`Imported ${response.data.imported} companies`);
    } catch (error) {
      console.error('Company import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import companies');
    } finally {
      setImportingCompanies(false);
    }
  };

  // Import leads
  const handleImportLeads = async () => {
    if (!leadCsvData) {
      toast.error('Please upload a leads CSV file');
      return;
    }

    // Validate required fields
    const hasFirstName = leadMappings['first_name'] || leadStaticValues.first_name;
    const hasCompanyName = leadMappings['company_name'] || leadStaticValues.company_name;
    
    if (!hasFirstName) {
      toast.error('First Name is required. Please map it or provide a static value.');
      return;
    }
    if (!hasCompanyName) {
      toast.error('Company Name is required. Please map it or provide a static value.');
      return;
    }

    setImportingLeads(true);
    
    try {
      // Build import data
      const importData = leadCsvData.rows.map((row, idx) => {
        const mapped = {};
        
        // Apply field mappings (fieldKey -> csvColumn)
        Object.entries(leadMappings).forEach(([fieldKey, csvCol]) => {
          if (csvCol && csvCol !== 'skip') {
            mapped[fieldKey] = row[csvCol] || '';
          }
        });
        
        // Apply static values
        Object.entries(leadStaticValues).forEach(([fieldKey, value]) => {
          if (value) {
            mapped[fieldKey] = value;
          }
        });
        
        // Handle full name split if first_name contains space (full name)
        if (mapped.first_name && mapped.first_name.includes(' ') && !mapped.last_name) {
          const nameParts = mapped.first_name.trim().split(' ');
          if (nameParts.length >= 2) {
            mapped.first_name = nameParts[0];
            mapped.last_name = nameParts.slice(1).join(' ');
          }
        }
        
        return mapped;
      });

      const response = await axios.post(`${API}/import/leads`, {
        leads_data: importData,
        skip_duplicates: skipDuplicates,
        duplicate_check_field: duplicateCheckField
      });

      setLeadResults(response.data);
      toast.success(`Imported ${response.data.imported} leads`);
      
      // Call the completion callback
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Lead import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import leads');
    } finally {
      setImportingLeads(false);
    }
  };

  // Navigation
  const goToLeads = () => {
    if (!skipCompanies && !companyResults) {
      toast.error('Please import companies first or check "Skip Companies Import"');
      return;
    }
    setStep(2);
  };

  const goBackToCompanies = () => {
    setStep(1);
  };

  const finishAndClose = () => {
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            CSV Import Wizard
            <Badge variant="outline" className="ml-auto">
              Step {step} of 2
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Companies Import */}
        {step === 1 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Step 1: Import Companies
                </CardTitle>
                <CardDescription>
                  Import company data first. This data will be used to link leads in the next step.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Info className="h-4 w-4 text-blue-600" />
                  <p className="text-sm text-blue-900">
                    Upload a CSV file with company information including name, address, and other details.
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="skip-companies"
                    checked={skipCompanies}
                    onCheckedChange={setSkipCompanies}
                  />
                  <Label htmlFor="skip-companies" className="cursor-pointer">
                    Skip companies import (companies already exist in the system)
                  </Label>
                </div>

                {!skipCompanies && (
                  <>
                    <div>
                      <Label>Upload Companies CSV</Label>
                      <div className="mt-2">
                        <input
                          ref={companyFileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleCompanyFileSelect}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => companyFileInputRef.current?.click()}
                          className="w-full"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          {companyFile ? companyFile.name : 'Choose CSV File'}
                        </Button>
                      </div>
                    </div>

                    {companyCsvData && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium">Map CSV Columns to Fields</h3>
                          <Badge>{companyCsvData.rows.length} rows</Badge>
                        </div>

                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[200px]">System Field</TableHead>
                              <TableHead className="w-[200px]">CSV Column</TableHead>
                              <TableHead className="w-[200px]">OR Static Value</TableHead>
                              <TableHead>Preview</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {COMPANY_FIELDS.map((field) => (
                              <TableRow key={field.key}>
                                <TableCell className="font-medium">
                                  {field.label} {field.required && <span className="text-red-500">*</span>}
                                  {field.hint && <p className="text-xs text-slate-400">{field.hint}</p>}
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={companyMappings[field.key] || 'skip'}
                                    onValueChange={(value) => {
                                      setCompanyMappings({ ...companyMappings, [field.key]: value === 'skip' ? '' : value });
                                      // Clear static value if mapping to CSV column
                                      if (value !== 'skip') {
                                        const newStatic = { ...companyStaticValues };
                                        delete newStatic[field.key];
                                        setCompanyStaticValues(newStatic);
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Skip field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="skip">Skip field</SelectItem>
                                      {companyCsvData.headers.map((header) => (
                                        <SelectItem key={header} value={header}>
                                          {header}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell>
                                  <Input
                                    placeholder="Type static value..."
                                    value={companyStaticValues[field.key] || ''}
                                    onChange={(e) => {
                                      setCompanyStaticValues({ ...companyStaticValues, [field.key]: e.target.value });
                                      // Clear CSV mapping if entering static value
                                      if (e.target.value) {
                                        const newMappings = { ...companyMappings };
                                        delete newMappings[field.key];
                                        setCompanyMappings(newMappings);
                                      }
                                    }}
                                    className="w-full"
                                  />
                                </TableCell>
                                <TableCell className="text-sm text-slate-600">
                                  {companyStaticValues[field.key] ? (
                                    <span className="text-green-600 font-medium">
                                      "{companyStaticValues[field.key]}"
                                    </span>
                                  ) : companyMappings[field.key] && companyCsvData.rows[0]?.[companyMappings[field.key]] ? (
                                    <>
                                      {companyCsvData.rows[0][companyMappings[field.key]].substring(0, 30)}
                                      {companyCsvData.rows[0][companyMappings[field.key]].length > 30 ? '...' : ''}
                                    </>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        <div className="flex gap-3 pt-4">
                          <Button
                            onClick={handleImportCompanies}
                            disabled={importingCompanies}
                            className="flex-1"
                          >
                            {importingCompanies ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Importing Companies...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="mr-2 h-4 w-4" />
                                Import Companies
                              </>
                            )}
                          </Button>
                        </div>

                        {companyResults && (
                          <Card className="bg-green-50 border-green-200">
                            <CardContent className="pt-6">
                              <div className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                                <div className="flex-1">
                                  <h4 className="font-semibold text-green-900">Companies Import Complete</h4>
                                  <div className="mt-2 text-sm text-green-800 space-y-1">
                                    <p>✓ Imported: {companyResults.imported}</p>
                                    <p>✓ Duplicates skipped: {companyResults.duplicates || 0}</p>
                                    {companyResults.errors?.length > 0 && (
                                      <p className="text-red-600">✗ Errors: {companyResults.errors.length}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button onClick={goToLeads} disabled={!skipCompanies && !companyResults}>
                    Next: Import Leads
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Leads Import */}
        {step === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Step 2: Import Leads
                </CardTitle>
                <CardDescription>
                  Import lead/contact data. Leads will be linked to companies by company name.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Info className="h-4 w-4 text-blue-600" />
                  <p className="text-sm text-blue-900">
                    Upload a CSV file with lead information. The Company Name column will be used to link leads to companies.
                  </p>
                </div>

                <div>
                  <Label>Upload Leads CSV</Label>
                  <div className="mt-2">
                    <input
                      ref={leadFileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleLeadFileSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => leadFileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {leadFile ? leadFile.name : 'Choose CSV File'}
                    </Button>
                  </div>
                </div>

                {leadCsvData && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">Map CSV Columns to Fields</h3>
                      <Badge>{leadCsvData.rows.length} rows</Badge>
                    </div>

                    {/* Duplicate Check Options */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-blue-900">Duplicate Detection</Label>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="skip-duplicates"
                            checked={skipDuplicates}
                            onCheckedChange={setSkipDuplicates}
                          />
                          <Label htmlFor="skip-duplicates" className="text-sm cursor-pointer">
                            Skip duplicate leads
                          </Label>
                        </div>
                        {skipDuplicates && (
                          <div className="flex items-center gap-2">
                            <Label className="text-sm text-slate-600">Check by:</Label>
                            <Select value={duplicateCheckField} onValueChange={setDuplicateCheckField}>
                              <SelectTrigger className="w-[180px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="work_email">Work Email</SelectItem>
                                <SelectItem value="personal_email">Personal Email</SelectItem>
                                <SelectItem value="linkedin_url">LinkedIn URL</SelectItem>
                                <SelectItem value="work_direct_phone">Work Phone</SelectItem>
                                <SelectItem value="mobile_phone">Mobile Phone</SelectItem>
                                <SelectItem value="full_name_company">Name + Company</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-blue-700">
                        {skipDuplicates 
                          ? `Leads with existing ${duplicateCheckField === 'work_email' ? 'work email' : duplicateCheckField === 'personal_email' ? 'personal email' : duplicateCheckField === 'linkedin_url' ? 'LinkedIn URL' : duplicateCheckField === 'work_direct_phone' ? 'work phone' : duplicateCheckField === 'mobile_phone' ? 'mobile phone' : 'name and company'} will be skipped.`
                          : 'All leads will be imported, even if duplicates exist.'
                        }
                      </p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <strong>💡 Full Name Split:</strong> If your CSV has full names in one column (e.g., "John Doe"), map it to <strong>First Name</strong>. 
                        The system will automatically split "John Doe" into First Name: "John" and Last Name: "Doe".
                      </p>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">System Field</TableHead>
                          <TableHead className="w-[200px]">CSV Column</TableHead>
                          <TableHead className="w-[200px]">OR Static Value</TableHead>
                          <TableHead>Preview</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {LEAD_FIELDS.map((field) => (
                          <TableRow key={field.key}>
                            <TableCell className="font-medium">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                              {field.hint && <p className="text-xs text-slate-400">{field.hint}</p>}
                            </TableCell>
                            <TableCell>
                              <Select
                                value={leadMappings[field.key] || 'skip'}
                                onValueChange={(value) => {
                                  setLeadMappings({ ...leadMappings, [field.key]: value === 'skip' ? '' : value });
                                  // Clear static value if mapping to CSV column
                                  if (value !== 'skip') {
                                    const newStatic = { ...leadStaticValues };
                                    delete newStatic[field.key];
                                    setLeadStaticValues(newStatic);
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Skip field" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="skip">Skip field</SelectItem>
                                  {leadCsvData.headers.map((header) => (
                                    <SelectItem key={header} value={header}>
                                      {header}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {field.key === 'status' ? (
                                <Select
                                  value={leadStaticValues[field.key] || 'none'}
                                  onValueChange={(value) => {
                                    if (value === 'none') {
                                      const newStatic = { ...leadStaticValues };
                                      delete newStatic[field.key];
                                      setLeadStaticValues(newStatic);
                                    } else {
                                      setLeadStaticValues({ ...leadStaticValues, [field.key]: value });
                                      // Clear CSV mapping if selecting static value
                                      const newMappings = { ...leadMappings };
                                      delete newMappings[field.key];
                                      setLeadMappings(newMappings);
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select status..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No default</SelectItem>
                                    {STATUS_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : field.key === 'lead_types' ? (
                                <Select
                                  value={leadStaticValues[field.key] || 'none'}
                                  onValueChange={(value) => {
                                    if (value === 'none') {
                                      const newStatic = { ...leadStaticValues };
                                      delete newStatic[field.key];
                                      setLeadStaticValues(newStatic);
                                    } else {
                                      setLeadStaticValues({ ...leadStaticValues, [field.key]: value });
                                      // Clear CSV mapping if selecting static value
                                      const newMappings = { ...leadMappings };
                                      delete newMappings[field.key];
                                      setLeadMappings(newMappings);
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select type..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No default</SelectItem>
                                    {LEAD_TYPE_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  placeholder="Type static value..."
                                  value={leadStaticValues[field.key] || ''}
                                  onChange={(e) => {
                                    setLeadStaticValues({ ...leadStaticValues, [field.key]: e.target.value });
                                    // Clear CSV mapping if entering static value
                                    if (e.target.value) {
                                      const newMappings = { ...leadMappings };
                                      delete newMappings[field.key];
                                      setLeadMappings(newMappings);
                                    }
                                  }}
                                  className="w-full"
                                />
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {leadStaticValues[field.key] ? (
                                <span className="text-green-600 font-medium">
                                  "{leadStaticValues[field.key]}"
                                </span>
                              ) : leadMappings[field.key] && leadCsvData.rows[0]?.[leadMappings[field.key]] ? (
                                <>
                                  {leadCsvData.rows[0][leadMappings[field.key]].substring(0, 30)}
                                  {leadCsvData.rows[0][leadMappings[field.key]].length > 30 ? '...' : ''}
                                </>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={handleImportLeads}
                        disabled={importingLeads}
                        className="flex-1"
                      >
                        {importingLeads ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Importing Leads...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Import Leads
                          </>
                        )}
                      </Button>
                    </div>

                    {leadResults && (
                      <Card className={leadResults.errors?.length > 0 ? "bg-yellow-50 border-yellow-200" : "bg-green-50 border-green-200"}>
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-3">
                            {leadResults.errors?.length > 0 ? (
                              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                            ) : (
                              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                            )}
                            <div className="flex-1">
                              <h4 className={`font-semibold ${leadResults.errors?.length > 0 ? 'text-yellow-900' : 'text-green-900'}`}>
                                Leads Import {leadResults.imported > 0 ? 'Complete! 🎉' : 'Failed'}
                              </h4>
                              <div className={`mt-2 text-sm space-y-1 ${leadResults.errors?.length > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                                <p>✓ Leads imported: {leadResults.imported}</p>
                                <p>✓ Duplicates skipped: {leadResults.duplicates || 0}</p>
                                <p>✓ Companies created: {leadResults.companies_created || 0}</p>
                                <p>✓ Locations created: {leadResults.locations_created || 0}</p>
                                {leadResults.errors?.length > 0 && (
                                  <>
                                    <p className="text-red-600 font-semibold mt-2">✗ Errors: {leadResults.errors.length}</p>
                                    <div className="mt-2 p-3 bg-white border border-red-200 rounded max-h-40 overflow-y-auto">
                                      {leadResults.errors.map((err, idx) => (
                                        <p key={idx} className="text-xs text-red-700 mb-1">
                                          Row {err.row}: {err.error}
                                        </p>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                <div className="flex justify-between gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={goBackToCompanies}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Companies
                  </Button>
                  <Button onClick={finishAndClose} disabled={!leadResults}>
                    Finish
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
